import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Lock, RefreshCw, Search } from "lucide-react"
import { smartoltService, type SmartOLTMap, type SmartOLTMapOdb, type SmartOLTMapOnu } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { SmartOLTMapCanvas } from "./SmartOLTMapCanvas.tsx"
import {
  COVERAGE_COLORS,
  FAULT_COLORS,
  filterMapOdbs,
  filterMapOnus,
  formatSignal,
  type CoverageKey,
  type FaultKey,
} from "./smartoltMapUtils.ts"

const empty: SmartOLTMap = {
  onus: [],
  odbs: [],
  summary: {
    onus: 0,
    odbs: 0,
    online: 0,
    los: 0,
    power: 0,
    other: 0,
    weak: 0,
    strong: 0,
    onus_without_gps: 0,
    odbs_without_gps: 0,
  },
}

const ALL_FAULTS: Record<FaultKey, boolean> = { online: true, los: true, power: true, other: true }
const ALL_COVERAGE: Record<CoverageKey, boolean> = { weak: true, strong: true }

export function SmartOLTMapsPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTMap>(empty)
  const [search, setSearch] = useState("")
  const [showOnus, setShowOnus] = useState(true)
  const [showOdbs, setShowOdbs] = useState(true)
  const [showCoverage, setShowCoverage] = useState(false)
  const [faults, setFaults] = useState(ALL_FAULTS)
  const [coverage, setCoverage] = useState(ALL_COVERAGE)
  const [selectedOnu, setSelectedOnu] = useState<SmartOLTMapOnu | null>(null)
  const [selectedOdb, setSelectedOdb] = useState<SmartOLTMapOdb | null>(null)

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getMap()
      setData({
        onus: Array.isArray(next?.onus) ? next.onus : [],
        odbs: Array.isArray(next?.odbs) ? next.odbs : [],
        summary: next?.summary || empty.summary,
      })
    } catch {
      toast.error("Failed to load map")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const visibleOnus = useMemo(
    () => (showOnus ? filterMapOnus(data.onus, { search, faults, coverage, showCoverage }) : []),
    [data.onus, search, faults, coverage, showCoverage, showOnus],
  )
  const visibleOdbs = useMemo(
    () => (showOdbs ? filterMapOdbs(data.odbs, search) : []),
    [data.odbs, search, showOdbs],
  )

  const toggleFault = (key: FaultKey) => setFaults((prev) => ({ ...prev, [key]: !prev[key] }))
  const toggleCoverage = (key: CoverageKey) => setCoverage((prev) => ({ ...prev, [key]: !prev[key] }))

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Network Maps</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Devices with SmartOLT GPS coordinates — fiber drawing is read-only.
            {data.summary.onus_without_gps ? ` ${data.summary.onus_without_gps} ONUs have no coordinates.` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 shadow-xs">{data.summary.onus} ONUs</span>
          <span className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 font-semibold text-blue-700 shadow-xs">{data.summary.odbs} ODBs</span>
          <span className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5 font-semibold text-rose-700 shadow-xs">{data.summary.los} LoS</span>
          <span className="rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-1.5 font-semibold text-indigo-700 shadow-xs">{data.summary.power} Power</span>
          <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 font-semibold text-slate-600 shadow-xs">
            <Lock className="h-3 w-3" /> Read-only
          </span>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-slate-200 bg-slate-50/80 p-3.5 sm:p-4">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search devices…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs"
            />
          </div>
          {[
            { label: "Device", checked: showOnus, onChange: (e: React.ChangeEvent<HTMLInputElement>) => setShowOnus(e.target.checked) },
            { label: "ODB", checked: showOdbs, onChange: (e: React.ChangeEvent<HTMLInputElement>) => setShowOdbs(e.target.checked) },
            { label: "Coverage", checked: showCoverage, onChange: (e: React.ChangeEvent<HTMLInputElement>) => setShowCoverage(e.target.checked) },
          ].map(({ label, checked, onChange }) => (
            <label key={label} className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50">
              <input type="checkbox" checked={checked} onChange={onChange} className="h-3.5 w-3.5 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500" />
              {label}
            </label>
          ))}
          <button type="button" disabled={refreshing} onClick={() => load()}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

          <div className="relative h-[calc(100vh-14rem)] min-h-[520px]">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">Loading map…</div>
            ) : (
              <SmartOLTMapCanvas
                onus={visibleOnus}
                odbs={visibleOdbs}
                showCoverage={showCoverage}
                selectedId={selectedOnu?.id || selectedOdb?.id}
                onSelectOnu={(row) => {
                  setSelectedOdb(null)
                  setSelectedOnu(row)
                }}
                onSelectOdb={(row) => {
                  setSelectedOnu(null)
                  setSelectedOdb(row)
                }}
              />
            )}

            <div className="pointer-events-none absolute right-3 top-3 z-[400] rounded-xl border border-slate-200 bg-white/95 backdrop-blur-md px-3 py-2.5 text-[11px] shadow-lg">
              <div className="mb-1.5 font-semibold text-slate-700">Faults:</div>
              <div className="pointer-events-auto flex flex-wrap gap-1.5">
                {([
                  ["los", "LoS", data.summary.los],
                  ["power", "Power", data.summary.power],
                  ["other", "Other", data.summary.other],
                ] as [FaultKey, string, number][]).map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleFault(key)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-all ${
                      faults[key]
                        ? "bg-slate-100 border border-slate-200 text-slate-800 shadow-xs font-semibold"
                        : "opacity-50 border border-transparent text-slate-500 hover:opacity-100"
                    }`}
                  >
                    <span className="h-2 w-2 rounded-sm" style={{ background: FAULT_COLORS[key] }} />
                    <span>{label}</span>
                    <span className="font-mono text-slate-600">{count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-xl border border-slate-200 bg-white/95 backdrop-blur-md px-3 py-2.5 text-[11px] shadow-lg">
              <div className="mb-1.5 font-semibold text-slate-700">Coverage:</div>
              <div className="pointer-events-auto flex flex-wrap gap-1.5">
                {([
                  ["weak", "Weak", data.summary.weak],
                  ["strong", "Strong", data.summary.strong],
                ] as [CoverageKey, string, number][]).map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleCoverage(key)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-all ${
                      coverage[key]
                        ? "bg-slate-100 border border-slate-200 text-slate-800 shadow-xs font-semibold"
                        : "opacity-50 border border-transparent text-slate-500 hover:opacity-100"
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: COVERAGE_COLORS[key] }} />
                    <span>{label}</span>
                    <span className="font-mono text-slate-600">{count}</span>
                  </button>
                ))}
              </div>
            </div>

            {(selectedOnu || selectedOdb) && (
              <div className="absolute bottom-3 right-3 z-[400] w-72 overflow-hidden rounded-xl border border-slate-200 bg-white/95 p-4 text-xs shadow-xl backdrop-blur-sm">
                {selectedOnu ? (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-900">{selectedOnu.name}</div>
                    <div className="text-slate-600">{selectedOnu.status_kind} · {formatSignal(selectedOnu.signal_rx)}</div>
                    <div className="text-slate-600">{[selectedOnu.olt_name, selectedOnu.board && selectedOnu.port ? `${selectedOnu.board}/${selectedOnu.port}` : null].filter(Boolean).join(" · ")}</div>
                    <div className="text-slate-500">{[selectedOnu.zone_name, selectedOnu.odb_name].filter(Boolean).join(" · ") || "—"}</div>
                    <div className="flex gap-3 pt-1 border-t border-slate-200">
                      <Link to={SMARTOLT_PATHS.onuDetail(selectedOnu.onu_external_id)} className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                        ONU details
                      </Link>
                      {selectedOnu.olt_id ? (
                        <Link to={SMARTOLT_PATHS.oltDetail(selectedOnu.olt_id)} className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                          OLT
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : selectedOdb ? (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-900">{selectedOdb.name}</div>
                    <div className="text-slate-600">{selectedOdb.zone_name || "ODB"}</div>
                    <Link to={SMARTOLT_PATHS.odbs} className="text-blue-600 hover:text-blue-800 hover:underline font-semibold">
                      ODB settings
                    </Link>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
